import React, { FC, ReactNode } from 'react';
import { create } from 'zustand';
import { ApiService } from '~/data/restApi';
import { globalPendingRequestTracker } from '~/editor/lib/PendingRequestTracker';
import { positionChangeQueue } from '~/editor/lib/PositionChangeQueue';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import { isTemplateSupportedForEditing } from '~/training/sequence/templates/utils/templateSupport';
import {
  markTrainingPublished as applyPublishedTimestamps,
  touchContentEditedAt
} from '~/editor/trainingContentTimestamps';

export interface SequenceItem {
  id: number | string;
  type?: string;
  position?: number;
  appear?: number;
  disappear?: number;
  items?: SequenceItem[];
  title?: string | null;
  subtitle?: string | null;
  body?: string | null;
  value?: string | number | boolean | null;
  subvalue?: string | null;
  icon?: string | null;
  variant?: string | null;
  correct?: boolean | null;
  feedback?: string | null;
  pairId?: string | null;
  coordinates?: string | null;
  branchSequenceId?: string | null;
  conversationBotId?: string | null;
  __typename?: string;
  typename?: string;
  pictures?: Array<{
    id: string | number;
    mediaUrl?: string;
    name?: string;
    [key: string]: unknown;
  }>;
  audios?: Array<{
    id: string | number;
    mediaUrl?: string;
    name?: string;
    [key: string]: unknown;
  }>;
  videos?: Array<{
    id: string | number;
    mediaUrl?: string;
    name?: string;
    [key: string]: unknown;
  }>;
  documents?: Array<{
    id: string | number;
    mediaUrl?: string;
    name?: string;
    [key: string]: unknown;
  }>;
  feedbackAudios?: Array<{
    mediaUrl?: string | null;
    subtitlesUrl?: string | null;
  }>;
  [key: string]: unknown;
}

export interface InternalSequenceSettings {
  surveyType: 'precourse' | 'postcourse' | 'custom' | null;
  personalization: boolean;
  legallyRequired: boolean;
  internalName: string | null;
  icon: 'text' | 'video' | 'question' | null;
  tags: string[];
  durationOverride: number | null;
  usedInCourses: {
    courses: Array<{ id: number; title: string; revision: number }>;
    total: number;
  };
}

export interface EditorSequence {
  id: number;
  items: SequenceItem[];
  editable?: boolean;
  templateName?: string;
  templateEditable?: boolean;
  legallyRequired?: boolean;
  internalSettings?: InternalSequenceSettings;
  [key: string]: unknown;
}

interface EditorChapterSequence {
  id?: number;
  position?: number;
  sequence: EditorSequence;
}

export interface EditorChapter {
  id?: number | string;
  position?: number;
  chapterSequences: EditorChapterSequence[];
  _rename?: boolean;
  [key: string]: unknown;
}

export interface EditorTraining {
  id: number | string;
  title?: string;
  status?: 'live' | 'draft' | string;
  /** Set by API (or temp client patch) for customized-canonical course flows */
  customizationOfCanonical?: boolean;
  isRepublish?: boolean;
  chapters: EditorChapter[];
  availableLocales?: Array<{
    locale: string;
    name?: string | null;
    text: string | null;
  }> | null;
  companyFeatures?: {
    courseAuthoring?: boolean;
  };
  canAssignLearners?: boolean;
  companyId?: number | string;
  shouldDisplayTranslateButton?: boolean;
  titleEditable?: boolean;
  lastPublishedAt?: string;
  contentEditedAt?: string;
}

export interface HistoryItem {
  sequenceId: number;
  elementId?: number | string;
  fieldType?: string;
  value?: unknown;
  oldValue?: unknown;
  type?: string;
  active: boolean;
  timestamp: number;
}

interface TemplateRecord {
  name: string;
  friendlyName: string;
  kind: string;
  [key: string]: unknown;
}

type GroupedTemplates = Partial<Record<string, TemplateRecord[]>>;

export interface EditorMediaFile {
  id: string | number;
  mediaUrl?: string;
  name?: string;
  [key: string]: unknown;
}

interface AudioFile {
  id: string | number;
  [key: string]: unknown;
}

export interface AddSubItemPayload {
  sequenceId: number;
  data: Record<string, unknown>;
  newItem: Record<string, unknown>;
  persistFile?: boolean;
  imageFile?: { id: string | number; [key: string]: unknown };
  skipStateUpdate?: boolean;
  onCreated?: (serverItem: Record<string, unknown>) => void;
  onError?: (error: unknown) => void;
}

interface AIModificationStatus {
  isLoading: boolean;
  error: string | null;
  success: boolean;
  lastPrompt: string;
}

interface SequenceMetadata {
  canUndo: boolean;
  canRedo: boolean;
}

interface ResponseWithMetadata {
  sequenceMetadata?: SequenceMetadata;
  sequence?: unknown;
  subitem?: unknown;
  error?: boolean;
  message?: string;
}

export type TranslationScope =
  | { kind: 'all' }
  | { kind: 'chapter'; chapterId: number }
  | { kind: 'sequence'; sequenceId: number };

interface GlobalPlaceholders {
  images: {
    horizontal: number | null;
    vertical: number | null;
  };
  audio: number | null;
  video: number | null;
}

interface Store {
  apiService: ApiService | null;
  training: EditorTraining | null;
  sequence: EditorSequence | null;
  translationScope: TranslationScope;
  setTranslationScope: (scope: TranslationScope) => void;
  setApiService: (
    token: string,
    companyId: number | string,
    trainingId?: number | string | null
  ) => void;
  refreshingTrainingOutline: boolean;
  refreshTrainingOutline: () => Promise<void>;
  lastChapterId: number | null;
  contentEditable: boolean;
  editorMode: boolean;
  isReadOnlyView: boolean;
  recomputeContentEditable: () => void;
  aiModification: AIModificationStatus;
  featureFlags: string[];
  setFeatureFlags: (flags: string[]) => void;
  hasFeatureFlag: (flagName: string) => boolean;
  canUndo: boolean;
  canRedo: boolean;
  setUndoRedoState: (canUndo: boolean, canRedo: boolean) => void;
  globalPlaceholders: GlobalPlaceholders | null;
  globalPlaceholdersLoading: boolean;
  globalPlaceholdersError: string | null;
  fetchGlobalPlaceholders: (force?: boolean) => Promise<void>;
  templates: GroupedTemplates | null;
  templatesLoading: boolean;
  templatesError: string | null;
  fetchTemplates: (force?: boolean) => Promise<void>;
  undoAction: (
    sequenceId: number,
    callback?: (sequenceId: number, training: EditorTraining) => void
  ) => Promise<void>;
  redoAction: (
    sequenceId: number,
    callback?: (sequenceId: number, training: EditorTraining) => void
  ) => Promise<void>;
  editSequence(data: EditorSequence): void;
  /**
   * Returns a promise that resolves once the underlying API write has settled.
   * Older fire-and-forget callers can keep ignoring the return value.
   */
  updateSequence(
    elementId: number | string,
    fieldType: string,
    value: unknown,
    type?: string
  ): Promise<unknown>;
  updateSequenceInStore(
    elementId: number | string,
    fieldType: string,
    value: unknown,
    type?: string
  ): void;
  updateSubItemBatch(
    elementId: number | string,
    fields: Record<string, unknown>
  ): Promise<{ error: boolean }>;
  setEditorMode(value: boolean): void;
  setTraining(data: EditorTraining): void;
  /** Bump `contentEditedAt` to now — call on a real user content edit. */
  markContentEdited(): void;
  /** On publish: set `lastPublishedAt` equal to `contentEditedAt`. */
  markTrainingPublished(): void;
  setSequence(data: EditorSequence): void;
  nextSequence(): void;
  prevSequence(): void;
  collectIds(obj: unknown): string;
  /**
   * Returns a promise that resolves once the new sub-item is created on the
   * backend (and any optional file persist has finished). Fire-and-forget
   * callers can still ignore the return value.
   */
  addSubItem(data: AddSubItemPayload): Promise<unknown>;
  applySubItemsToState(
    items: Array<{ data: SubItemStatePayload; serverItem: ServerItemResponse }>
  ): void;
  /**
   * Returns a promise that resolves once the backend has actually deleted the
   * sub-item. Local state is updated synchronously regardless.
   */
  removeSubItem(subItemId: number | string): Promise<unknown>;
  updateSubItem(
    subItemId: number | string,
    fieldType: string,
    value: unknown
  ): Promise<{ error?: boolean; message?: string }>;
  updateFile(
    id: string,
    data: EditorMediaFile,
    type: string,
    parentId?: string
  ): void;
  setNarration(audio?: AudioFile): void;
  updateChapter(chapterId: number, data: Record<string, unknown>): void;
  createNewChapter(chapterName: string): void;
  resetAIModificationStatus: () => void;
  nextTooltip: () => void;
  setAIModificationStatus: (status: Partial<AIModificationStatus>) => void;
  modifySequenceWithAI(instructions: string): Promise<{
    success?: boolean;
    error?: boolean;
    message?: string;
    sequence?: EditorSequence;
  }>;
  showTooltips: boolean;
  setShowTooltips: (showTooltips: boolean) => void;
  tooltip: number;
  prevTooltip: () => void;
  history: HistoryItem[];
  undo: (cb: (sequenceId: number, training: EditorTraining) => void) => void;
  redo: (cb: (sequenceId: number, training: EditorTraining) => void) => void;
  clearHistory: () => void;
  forceUpdateSequence: boolean;
  setForceUpdateSequence: (forceUpdateSequence: boolean) => void;
  removeHistoryForSequence: (sequenceId: number) => void;
  updateTrainingTitle: (title: string) => void;
  shouldDisplayUpdateTranslationsModal: boolean;
  setDisplayUpdateTranslationsModal: (display: boolean) => void;
  shouldAutoUpdateTranslations: boolean;
  setShouldAutoUpdateTranslations: (
    shouldAutoUpdateTranslations: boolean
  ) => void;
}

export type TranslationStore = Pick<
  Store,
  'apiService' | 'training' | 'translationScope' | 'setTranslationScope'
>;

const HISTORY_STORAGE_KEY = 'easyllame_editor_history';

const saveHistoryToStorage = (history: HistoryItem[]) => {
  try {
    sessionStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
  } catch (error) {
    console.warn('Failed to save history to sessionStorage:', error);
  }
};

const loadHistoryFromStorage = (): HistoryItem[] => {
  try {
    const stored = sessionStorage.getItem(HISTORY_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.warn('Failed to load history from sessionStorage:', error);
    return [];
  }
};

const extractAndStoreMetadata = (
  response: ResponseWithMetadata,
  state: Pick<Store, 'hasFeatureFlag' | 'setUndoRedoState'>
) => {
  if (
    response?.sequenceMetadata &&
    state.hasFeatureFlag('course_editor_backend_undo_redo')
  ) {
    state.setUndoRedoState(
      response.sequenceMetadata.canUndo,
      response.sequenceMetadata.canRedo
    );
  }
};

const preserveLegallyRequired = (
  sequenceData: EditorSequence,
  sequenceId: number,
  training: EditorTraining | null
): EditorSequence => {
  if (!training || !training.chapters) {
    return sequenceData;
  }

  const oldSequenceInTraining = training.chapters
    .flatMap(c => c.chapterSequences)
    .find(cs => cs?.sequence?.id === sequenceId);

  const shouldPreserveLegallyRequired =
    oldSequenceInTraining?.sequence?.legallyRequired === true;

  return {
    ...sequenceData,
    ...(sequenceData.legallyRequired !== undefined
      ? { legallyRequired: sequenceData.legallyRequired }
      : shouldPreserveLegallyRequired
        ? { legallyRequired: true }
        : {})
  };
};

const _updateSequenceState = (
  state: Pick<Store, 'sequence' | 'training' | 'history'>,
  elementId: number | string,
  fieldType: string,
  value: unknown,
  type?: string
) => {
  let _type = type;

  //we need to find the type of the element
  if (_type == undefined && fieldType == 'value') {
    const findItemById = (
      items: SequenceItem[],
      id: string | number
    ): SequenceItem | null => {
      for (const item of items) {
        if (item.id == id) {
          return item;
        }
        if (item.items && item.items.length > 0) {
          const found = findItemById(item.items, id);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    const foundItem = findItemById(state.sequence!.items || [], elementId);
    _type = foundItem?.type;
  }

  let _fieldType = fieldType;

  // i know. I also love our backend code.
  // we need to use it, because backend team have many issues with files and they don't have 'value' field for buttons
  if (_type == 'QUESTION_FIELD' && fieldType == 'title') {
    _fieldType = 'value';
  }

  // update history
  const _history = state.history;

  let lastActiveIndex = -1;
  for (let i = _history.length - 1; i >= 0; i--) {
    if (
      _history[i].sequenceId === state.sequence!.id &&
      _history[i].active === true
    ) {
      lastActiveIndex = i;
      break;
    }
  }

  if (lastActiveIndex >= 0) {
    _history.splice(lastActiveIndex + 1);
  }

  if (_history.length > 0) {
    const lastHistory = _history[_history.length - 1];
    if (
      lastHistory.sequenceId === state.sequence!.id &&
      lastHistory.elementId == elementId &&
      lastHistory.fieldType == fieldType &&
      lastHistory.value == value &&
      lastHistory.type == type
    ) {
      return { ...state };
    }
  }

  const updateItems = (items: SequenceItem[]): SequenceItem[] => {
    return items?.map(item => {
      if (item.id == elementId) {
        const existingHistoryItem = _history.find(
          historyItem =>
            historyItem.sequenceId === state.sequence!.id &&
            historyItem.elementId === elementId &&
            historyItem.fieldType === fieldType &&
            historyItem.value === value
        );

        if (!existingHistoryItem) {
          if (
            !_history.find(
              historyItem => historyItem.sequenceId == state.sequence!.id
            )
          ) {
            _history.push({
              sequenceId: state.sequence!.id,
              elementId: undefined,
              fieldType: undefined,
              value: undefined,
              oldValue: undefined,
              type: undefined,
              active: false,
              timestamp: Date.now()
            });
          }

          if (value != item[_fieldType]) {
            _history.forEach(historyItem => {
              if (historyItem.sequenceId == state.sequence!.id) {
                historyItem.active = false;
              }
            });

            _history.push({
              sequenceId: state.sequence!.id,
              elementId,
              fieldType,
              value,
              oldValue: item[_fieldType],
              type,
              active: true,
              timestamp: Date.now()
            });
          }
        }

        return {
          ...item,
          [_fieldType]: value
        };
      }
      return item.items ? { ...item, items: updateItems(item.items) } : item;
    });
  };

  const updatedTraining = {
    ...state.training!,
    chapters: state.training!.chapters.map(chapter => ({
      ...chapter,
      chapterSequences: chapter.chapterSequences.map(seq => {
        return {
          ...seq,
          sequence: {
            ...seq.sequence,
            items: updateItems(seq.sequence.items)
          }
        };
      })
    }))
  };

  saveHistoryToStorage(_history);

  return {
    ...state,
    history: _history,
    sequence: preserveLegallyRequired(
      {
        ...state.sequence!,
        items: updateItems(state.sequence!.items)
      },
      state.sequence!.id,
      state.training
    ),
    training: updatedTraining
  };
};

function insertAndReorder(
  items: SequenceItem[],
  newItem: SequenceItem
): SequenceItem[] {
  const sorted = [...items].sort((a, b) => a.position! - b.position!);
  const result: SequenceItem[] = [];
  let inserted = false;

  for (const item of sorted) {
    if (!inserted && item.position! >= newItem.position!) {
      result.push({ ...newItem });
      inserted = true;
    }
    const newPosition = inserted ? item.position! + 1 : item.position;
    result.push({ ...item, position: newPosition });
  }

  if (!inserted) {
    result.push({ ...newItem });
  }

  return result;
}

function attachSubItemToParent(
  items: SequenceItem[],
  created: SequenceItem,
  parentId: string | number
): SequenceItem[] {
  return items?.map(it => {
    if (it.id == parentId) {
      const childItems = [...(it.items || []), created];
      // ensure ordering when parent-specific (we rely on explicit position inside each child if present)
      childItems.sort((a, b) => (a.position || 0) - (b.position || 0));
      return { ...it, items: childItems };
    }
    return it.items
      ? { ...it, items: attachSubItemToParent(it.items, created, parentId) }
      : it;
  });
}

export interface ServerItemResponse {
  subitem: SequenceItem;
  [key: string]: unknown;
}

export interface SubItemStatePayload {
  data: Record<string, unknown>;
  newItem: Record<string, unknown>;
}

function applySubItemToSequenceState(
  current: Store,
  data: SubItemStatePayload,
  serverItem: ServerItemResponse
): Store {
  if (!current.sequence) return current;

  const subitem = { ...serverItem.subitem };
  const authoritativePosition = data.data?.position as number | undefined;
  if (
    subitem.position &&
    authoritativePosition &&
    subitem.position < authoritativePosition
  ) {
    subitem.position = authoritativePosition;
  }

  const created = { ...data.newItem, id: subitem.id } as SequenceItem;

  const parentId = data.data?.parent_id as string | number | undefined;

  let allItems: SequenceItem[];
  if (parentId) {
    allItems = attachSubItemToParent(current.sequence.items, created, parentId);
  } else {
    const merged = {
      ...subitem,
      ...data.newItem,
      id: subitem.id,
      position: subitem.position
    } as SequenceItem;
    allItems = insertAndReorder(current.sequence.items, merged);
  }

  const updatedSequence = preserveLegallyRequired(
    { ...current.sequence, items: allItems },
    current.sequence.id,
    current.training
  );

  const updatedTraining: EditorTraining = {
    ...current.training!,
    chapters: current.training!.chapters.map((chapter: EditorChapter) => ({
      ...chapter,
      chapterSequences: chapter.chapterSequences.map(
        (seq: EditorChapterSequence) =>
          seq.sequence.id === current.sequence!.id
            ? { ...seq, sequence: updatedSequence }
            : seq
      )
    }))
  };

  return {
    ...current,
    sequence: updatedSequence,
    training: updatedTraining
  };
}

export const useEditorStore = create<Store>((set, get) => ({
  apiService: null as ApiService | null,
  forceUpdateSequence: false,
  setForceUpdateSequence: (forceUpdateSequence: boolean) =>
    set({ forceUpdateSequence }),
  translationScope: { kind: 'all' } as TranslationScope,
  setTranslationScope: (scope: TranslationScope) =>
    set({ translationScope: scope }),
  setApiService: (token, companyId, trainingId) =>
    set({
      apiService: new ApiService(token, companyId, trainingId, () =>
        get().refreshTrainingOutline()
      )
    }),
  refreshingTrainingOutline: false,
  refreshTrainingOutline: async () => {
    const { apiService, training, refreshingTrainingOutline } = get();
    const hasPendingWrites = () =>
      globalPendingRequestTracker.hasPendingRequests() ||
      positionChangeQueue.hasPendingRequests();
    if (
      !apiService ||
      !training?.id ||
      !Array.isArray(training.chapters) ||
      refreshingTrainingOutline ||
      training.chapters.some(chapter => chapter._rename) ||
      hasPendingWrites()
    )
      return;

    set({ refreshingTrainingOutline: true });
    try {
      const data = await apiService.getCourseInfo(training.id, {
        silent: true
      });
      // Never apply an older outline over a local edit or a different course.
      if (
        data?.error ||
        !data?.training?.chapters ||
        get().apiService !== apiService ||
        get().training !== training ||
        hasPendingWrites()
      )
        return;

      const localSequences = new Map(
        training.chapters
          .flatMap(chapter => chapter.chapterSequences)
          .map(cs => [cs.sequence.id, cs.sequence])
      );
      set({
        training: {
          ...training,
          chapters: data.training.chapters.map((chapter: EditorChapter) => ({
            ...chapter,
            chapterSequences: chapter.chapterSequences.map(cs => ({
              ...cs,
              sequence: localSequences.get(cs.sequence.id) || cs.sequence
            }))
          }))
        }
      });
    } catch {
      // Keep the current outline/canvas if the background refresh is unavailable.
    } finally {
      set({ refreshingTrainingOutline: false });
    }
  },
  featureFlags: [],
  setFeatureFlags: (flags: string[]) => set({ featureFlags: flags }),
  hasFeatureFlag: (flagName: string) => {
    return get().featureFlags.includes(flagName);
  },
  canUndo: false,
  canRedo: false,
  setUndoRedoState: (canUndo: boolean, canRedo: boolean) => {
    set({ canUndo, canRedo });
  },
  globalPlaceholders: null,
  globalPlaceholdersLoading: false,
  globalPlaceholdersError: null,
  fetchGlobalPlaceholders: async (force = false) => {
    const state = get();

    if (!state.apiService) {
      set({ globalPlaceholdersError: 'API service not initialized' });
      return;
    }

    if (state.globalPlaceholders && !force) {
      return;
    }

    set({ globalPlaceholdersLoading: true, globalPlaceholdersError: null });

    try {
      const result = await state.apiService.fetchGlobalPlaceholders();

      if (result.error) {
        set({
          globalPlaceholdersLoading: false,
          globalPlaceholdersError:
            result.message || 'Failed to fetch global placeholders'
        });
      } else {
        set({
          globalPlaceholders: result,
          globalPlaceholdersLoading: false,
          globalPlaceholdersError: null
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      set({
        globalPlaceholdersLoading: false,
        globalPlaceholdersError: message
      });
    }
  },
  templates: null,
  templatesLoading: false,
  templatesError: null,
  fetchTemplates: async (force = false) => {
    const state = get();

    if (!state.apiService) {
      set({ templatesError: 'API service not initialized' });
      return;
    }

    if (state.templates && !force) {
      return;
    }

    set({ templatesLoading: true, templatesError: null });

    try {
      const result = await state.apiService.getTemplates();

      if (result.error) {
        set({
          templates: null,
          templatesLoading: false,
          templatesError: result.message || 'Failed to fetch templates'
        });
        return;
      }

      set({
        templates: result,
        templatesLoading: false,
        templatesError: null
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      set({
        templates: null,
        templatesLoading: false,
        templatesError: message
      });
    }
  },
  history: loadHistoryFromStorage(),

  clearHistory: () => {
    sessionStorage.removeItem(HISTORY_STORAGE_KEY);
    set({ history: [] });
  },

  undo: (cb: (sequenceId: number, training: EditorTraining) => void) =>
    set(state => {
      // state.setForceUpdateSequence(false);

      const currentHistory = state.history.filter(
        item => item.sequenceId === state.sequence!.id
      );

      if (currentHistory.length === 0) {
        return { ...state };
      }

      const currentActiveIndex = currentHistory.findIndex(
        item => item.active === true
      );

      state.history.forEach(item => {
        if (item.sequenceId === state.sequence!.id) {
          item.active = false;
        }
      });

      if (currentActiveIndex > 0) {
        const previousHistory = currentHistory[currentActiveIndex - 1];
        previousHistory.active = true;

        const currentElement = currentHistory[currentActiveIndex];

        if (
          currentElement &&
          currentElement.elementId &&
          currentElement.fieldType &&
          currentElement.oldValue
        ) {
          if (currentElement.fieldType == 'pictures') {
            state.apiService
              ?.updateSubItem(
                currentElement.elementId,
                'picture_ids',
                (currentElement.oldValue as EditorMediaFile[]).map(
                  file => file.id
                )
              )
              .then(() => {
                cb(currentElement.sequenceId, state.training!);
              });
          } else if (currentElement.fieldType == 'videos') {
            state.apiService
              ?.updateSubItem(
                currentElement.elementId,
                'video_ids',
                (currentElement.oldValue as EditorMediaFile[]).map(
                  file => file.id
                )
              )
              .then(() => {
                cb(currentElement.sequenceId, state.training!);
              });
          } else if (currentElement.fieldType == 'documents') {
            state.apiService
              ?.updateSubItem(
                currentElement.elementId,
                'document_ids',
                (currentElement.oldValue as EditorMediaFile[]).map(
                  file => file.id
                )
              )
              .then(() => {
                cb(currentElement.sequenceId, state.training!);
              });
          } else {
            const apiFieldType =
              currentElement.type === 'QUESTION_FIELD' &&
              currentElement.fieldType === 'value'
                ? 'title'
                : currentElement.fieldType;

            state.apiService
              ?.updateSubItem(
                currentElement.elementId,
                apiFieldType,
                currentElement.oldValue
              )
              .then(() => {
                cb(currentElement.sequenceId, state.training!);
              });
          }
        }

        saveHistoryToStorage(state.history);

        return { ...state };
      }

      saveHistoryToStorage(state.history);
      return { ...state };
    }),

  redo: (cb: (sequenceId: number, training: EditorTraining) => void) =>
    set(state => {
      // state.setForceUpdateSequence(false);

      const currentHistory = state.history.filter(
        item => item.sequenceId === state.sequence!.id
      );

      if (currentHistory.length == 0) {
        return { ...state };
      }

      const currentActiveIndex = currentHistory.findIndex(
        item => item.active === true
      );

      state.history.forEach(item => {
        if (item.sequenceId === state.sequence!.id) {
          item.active = false;
        }
      });

      if (
        currentActiveIndex >= 0 &&
        currentActiveIndex < currentHistory.length - 1
      ) {
        const nextHistory = currentHistory[currentActiveIndex + 1];
        nextHistory.active = true;

        if (
          nextHistory &&
          nextHistory.elementId &&
          nextHistory.fieldType &&
          nextHistory.value
        ) {
          if (nextHistory.fieldType == 'pictures') {
            state.apiService
              ?.updateSubItem(
                nextHistory.elementId,
                'picture_ids',
                (nextHistory.value as EditorMediaFile[]).map(file => file.id)
              )
              .then(() => {
                cb(nextHistory.sequenceId, state.training!);
              });
          } else if (nextHistory.fieldType == 'videos') {
            state.apiService
              ?.updateSubItem(
                nextHistory.elementId,
                'video_ids',
                (nextHistory.value as EditorMediaFile[]).map(file => file.id)
              )
              .then(() => {
                cb(nextHistory.sequenceId, state.training!);
              });
          } else if (nextHistory.fieldType == 'documents') {
            state.apiService
              ?.updateSubItem(
                nextHistory.elementId,
                'document_ids',
                (nextHistory.value as EditorMediaFile[]).map(file => file.id)
              )
              .then(() => {
                cb(nextHistory.sequenceId, state.training!);
              });
          } else {
            const apiFieldType =
              nextHistory.type === 'QUESTION_FIELD' &&
              nextHistory.fieldType === 'value'
                ? 'title'
                : nextHistory.fieldType;

            state.apiService
              ?.updateSubItem(
                nextHistory.elementId,
                apiFieldType,
                nextHistory.value
              )
              .then(() => {
                cb(nextHistory.sequenceId, state.training!);
              });
          }
        }

        saveHistoryToStorage(state.history);

        return { ...state };
      }

      saveHistoryToStorage(state.history);

      return { ...state };
    }),
  training: [] as unknown as EditorTraining,
  sequence: null,
  sequences: [],
  lastChapterId: null,
  contentEditable: false,
  editorMode: false,
  isReadOnlyView: false,
  aiModification: {
    isLoading: false,
    error: null,
    success: false,
    lastPrompt: ''
  },
  showTooltips: false,
  setShowTooltips: (showTooltips: boolean) => set({ showTooltips }),
  tooltip: 0,
  nextTooltip: () => set(state => ({ tooltip: state.tooltip + 1 })),
  prevTooltip: () =>
    set(state => ({ tooltip: Math.max(0, state.tooltip - 1) })),
  collectIds: (obj: unknown) => {
    const ids: (number | string)[] = [];
    const traverse = (node: unknown): void => {
      if (node && typeof node === 'object') {
        const record = node as Record<string, unknown>;
        if (Object.prototype.hasOwnProperty.call(record, 'id')) {
          ids.push(record['id'] as number | string);
        }
        for (const key in record) {
          if (Object.prototype.hasOwnProperty.call(record, key)) {
            traverse(record[key]);
          }
        }
      }
    };

    traverse(obj);
    return ids.join('-');
  },
  editSequence: sequence =>
    set(state => ({
      ...state,
      sequence: preserveLegallyRequired(sequence, sequence.id, state.training)
    })),
  nextSequence: () =>
    set(state => {
      let nextSequence = state.sequence;
      let currentSequenceFind = false;

      state.training!.chapters.forEach(chapter => {
        chapter.chapterSequences.forEach(seq => {
          if (currentSequenceFind) {
            currentSequenceFind = false;
            nextSequence = seq.sequence;
          }

          if (seq.sequence.id === state.sequence!.id) {
            currentSequenceFind = true;
          }
        });
      });

      if (nextSequence!.id) {
        state.apiService?.getSequenceInfo(nextSequence!.id).then(response => {
          if (!response?.sequence?.id) return;
          state.setSequence(response.sequence);
          extractAndStoreMetadata(response, state);
        });
      }
      return { ...state };
    }),
  prevSequence: () =>
    set(state => {
      let prevSequence = state.sequence;
      let stopSearch = false;

      state.training!.chapters.forEach(chapter => {
        chapter.chapterSequences.forEach(seq => {
          if (seq.sequence.id != state.sequence!.id && !stopSearch) {
            prevSequence = seq.sequence;
          }

          if (seq.sequence.id === state.sequence!.id) {
            stopSearch = true;
          }
        });
      });

      if (prevSequence!.id) {
        state.apiService?.getSequenceInfo(prevSequence!.id).then(response => {
          if (!response?.sequence?.id) return;
          state.setSequence(response.sequence);
          extractAndStoreMetadata(response, state);
        });
      }

      return { ...state };
    }),
  updateSequenceInStore: (elementId, fieldType, value, type) => {
    set(
      prevState =>
        _updateSequenceState(
          prevState,
          elementId,
          fieldType,
          value,
          type
        ) as Store
    );
  },
  updateSequence: (elementId, fieldType, value, type) => {
    const state = get();

    if (!state.apiService) {
      console.error('ApiService is not initialized.');
      return Promise.resolve({ error: true });
    }

    let _type = type;

    // Detect type from element if not provided
    if (_type == undefined && fieldType == 'value') {
      const findItemById = (
        items: SequenceItem[],
        id: string | number
      ): SequenceItem | null => {
        for (const item of items) {
          if (item.id == id) {
            return item;
          }
          if (item.items && item.items.length > 0) {
            const found = findItemById(item.items, id);
            if (found) {
              return found;
            }
          }
        }
        return null;
      };

      const foundItem = findItemById(state.sequence!.items || [], elementId);
      _type = foundItem?.type;
    }

    // Capture the API promise so callers can `await` the actual server write.
    // i know, it is strange — QUESTION_FIELD / value sends as title.
    const apiPromise =
      _type == 'QUESTION_FIELD' && fieldType == 'value'
        ? state.apiService.updateSubItem(elementId, 'title', value)
        : state.apiService.updateSubItem(elementId, fieldType, value);

    const resultPromise = apiPromise.then(response => {
      extractAndStoreMetadata(response, state);
      return response;
    });

    // Update local state immediately (optimistic).
    set(
      prevState =>
        _updateSequenceState(
          prevState,
          elementId,
          fieldType,
          value,
          type
        ) as Store
    );

    // Real user edit (text / position / coordinates / etc.) — mark content dirty.
    get().markContentEdited();

    return resultPromise;
  },
  setEditorMode: value => {
    set({ editorMode: value });
    get().recomputeContentEditable();
  },
  recomputeContentEditable: () => {
    const state = get();
    const { editorMode, sequence } = state;

    if (!editorMode) {
      set({
        contentEditable: false,
        isReadOnlyView: false
      });
      return;
    }

    const isContentEditable =
      sequence?.editable !== false && isTemplateSupportedForEditing(sequence);

    set({
      contentEditable: isContentEditable,
      isReadOnlyView: editorMode && !isContentEditable
    });
  },
  setTraining: (data: EditorTraining) =>
    set(state => ({ ...state, training: data })),
  markContentEdited: () =>
    set(state =>
      state.training
        ? { ...state, training: touchContentEditedAt(state.training) }
        : state
    ),
  markTrainingPublished: () =>
    set(state =>
      state.training
        ? { ...state, training: applyPublishedTimestamps(state.training) }
        : state
    ),
  setSequence: (data: EditorSequence) => {
    set(state => ({
      ...state,
      sequence: preserveLegallyRequired(data, data.id, state.training)
    }));
    get().recomputeContentEditable();
  },

  undoAction: async (
    sequenceId: number,
    callback?: (sequenceId: number, training: EditorTraining) => void
  ) => {
    const state = get();
    if (!state.apiService) {
      console.error('ApiService is not initialized.');
      return;
    }

    try {
      const response = await state.apiService.undoSequence(sequenceId);

      if (response.error) {
        console.error('Undo failed:', response.message);
        return;
      }

      extractAndStoreMetadata(response, state);

      if (callback) {
        callback(sequenceId, state.training!);
      }
    } catch (error) {
      console.error('Undo failed:', error);
      throw error;
    }
  },

  redoAction: async (
    sequenceId: number,
    callback?: (sequenceId: number, training: EditorTraining) => void
  ) => {
    const state = get();
    if (!state.apiService) {
      console.error('ApiService is not initialized.');
      return;
    }

    try {
      const response = await state.apiService.redoSequence(sequenceId);

      if (response.error) {
        console.error('Redo failed:', response.message);
        return;
      }

      extractAndStoreMetadata(response, state);

      if (callback) {
        callback(sequenceId, state.training!);
      }
    } catch (error) {
      console.error('Redo failed:', error);
      throw error;
    }
  },

  addSubItem: (data: AddSubItemPayload) => {
    const state = get();
    if (!state.apiService) {
      console.error('ApiService is not initialized.');
      return Promise.resolve();
    }

    const api = state.apiService;

    // Trigger a rerender (parity with previous `set(state => ({...state}))`).
    set(prev => ({ ...prev }));

    return api
      .addSubItem(data.sequenceId, data.data)
      .then(async newItem => {
        extractAndStoreMetadata(newItem, state);

        // restApi.addSubItem does not throw on HTTP errors — it resolves with
        // `{ error: true, ... }` (no `subitem`). Bail out early so we don't
        // dereference `newItem.subitem.id` or push a malformed item into
        // sequence state. Toast is already emitted by restApi.
        if (newItem && (newItem as { error?: boolean }).error) {
          if (typeof data.onError === 'function') {
            try {
              data.onError(newItem);
            } catch {
              /* ignore */
            }
          }
          return newItem;
        }

        // optional file persist before updating state
        if (data.persistFile && data.imageFile && data.imageFile.id && api) {
          try {
            await api.updateSubItem(newItem.subitem.id, 'picture_ids', [
              data.imageFile.id
            ]);
            newItem.subitem.pictures = [data.imageFile];
          } catch (e) {
            console.warn('initial file persist failed', e);
          }
        }

        if (!data.skipStateUpdate) {
          set(current => applySubItemToSequenceState(current, data, newItem));
        }

        // Fire optional creation callback after state commit
        if (typeof data.onCreated === 'function') {
          try {
            data.onCreated(newItem);
          } catch (e) {
            /* ignore */
          }
        }

        return newItem;
      })
      .catch(e => {
        console.warn('addSubItem failed', e);
        if (typeof data.onError === 'function') {
          try {
            data.onError(e);
          } catch {
            /* ignore */
          }
        }
        // Preserve the original fire-and-forget contract (no rejection) so
        // existing call sites that do not `await` don't trigger Unhandled
        // Promise Rejections. Awaiting callers (e.g. AddCards.handleSubmit)
        // can still detect the failure via the returned `{ error: true }`.
        return {
          error: true,
          message: e instanceof Error ? e.message : 'addSubItem failed'
        };
      });
  },
  applySubItemsToState: (
    items: Array<{ data: SubItemStatePayload; serverItem: ServerItemResponse }>
  ) =>
    set(current => {
      return items.reduce(
        (state, { data, serverItem }) =>
          applySubItemToSequenceState(state, data, serverItem),
        current
      );
    }),
  removeSubItem: (subItemId: number | string) => {
    const state = get();
    if (!state.apiService) {
      console.error('ApiService is not initialized.');
      return Promise.resolve({ error: true });
    }
    const api = state.apiService;

    // Capture the real API promise so batch callers can await actual deletion.
    const apiPromise = api.removeSubItem(subItemId).then(response => {
      extractAndStoreMetadata(response, state);
      return response;
    });

    set(state => {
      const _history = state.history.filter(
        item => item.elementId != subItemId
      );

      const activeItem = state.history.find(
        item => item.elementId == subItemId && item.active === true
      );

      if (activeItem) {
        const activeIndex = state.history.findIndex(
          item => item.elementId == subItemId && item.active === true
        );

        for (let i = activeIndex - 1; i >= 0; i--) {
          const previousItem = state.history[i];
          if (previousItem.elementId != subItemId) {
            const itemToActivate = _history.find(
              item =>
                item.elementId == previousItem.elementId &&
                item.timestamp === previousItem.timestamp
            );
            if (itemToActivate) {
              itemToActivate.active = true;
            }
            break;
          }
        }
      }

      saveHistoryToStorage(_history);
      set({ history: _history });

      const removeItemById = (
        items: SequenceItem[],
        id: number | string
      ): SequenceItem[] => {
        const filteredItems = items
          .filter(item => item.id != id)
          .map(item => {
            return item.items
              ? { ...item, items: removeItemById(item.items, id) }
              : item;
          });

        const sortedItems = filteredItems.sort(
          (a, b) => a.position! - b.position!
        );

        let gapIndex = -1;
        for (let i = 0; i < sortedItems.length; i++) {
          if (sortedItems[i].position !== i + 1) {
            gapIndex = i;
            break;
          }
        }

        if (gapIndex === -1) {
          return sortedItems;
        }

        return sortedItems.map((item, index) => ({
          ...item,
          position: index + 1
        }));
      };

      const updatedSequence = preserveLegallyRequired(
        {
          ...state.sequence!,
          items: removeItemById(state.sequence!.items, subItemId)
        },
        state.sequence!.id,
        state.training
      );

      const updatedTraining = {
        ...state.training!,
        chapters: state.training!.chapters.map(chapter => ({
          ...chapter,
          chapterSequences: chapter.chapterSequences.map(seq => {
            return seq.sequence.id === state.sequence!.id
              ? { ...seq, sequence: updatedSequence }
              : seq;
          })
        }))
      };

      return {
        ...state,
        sequence: updatedSequence,
        training: updatedTraining,
        history: _history
      };
    });

    return apiPromise;
  },
  updateSubItem: async (
    subItemId: number | string,
    fieldType: string,
    value: unknown
  ) => {
    const state = get();

    if (!state.apiService) {
      console.error('ApiService is not initialized.');
      return { error: true, message: 'API service not available' };
    }

    try {
      const response = await state.apiService.updateSubItem(
        subItemId,
        fieldType,
        value
      );

      extractAndStoreMetadata(response, state);

      if (!response.error) {
        if (
          fieldType === 'item_bots_attributes' &&
          (value as Record<string, unknown>[])?.[0]?.conversation_bot_id
        ) {
          state.updateSequence(
            subItemId,
            'conversationBotId',
            (value as Record<string, unknown>[])[0].conversation_bot_id
          );
        } else {
          state.updateSequence(subItemId, fieldType, value);
        }

        return { error: false };
      }

      return response;
    } catch (error) {
      console.error('Error updating subitem:', error);
      return { error: true, message: 'Failed to update subitem' };
    }
  },
  updateSubItemBatch: async (
    elementId: number | string,
    fields: Record<string, unknown>
  ) => {
    const state = get();

    if (!state.apiService) {
      console.error('ApiService is not initialized.');
      return { error: true };
    }

    try {
      const response = await state.apiService.updateSubItemBatch(
        elementId,
        fields
      );

      extractAndStoreMetadata(response, state);

      if (!response.error) {
        // Update state for all fields WITHOUT making additional API calls
        set(prevState => {
          let updatedState: Store = prevState;
          Object.entries(fields).forEach(([fieldType, value]) => {
            updatedState = _updateSequenceState(
              updatedState,
              elementId,
              fieldType,
              value
            ) as Store;
          });
          return updatedState;
        });

        return { error: false };
      }

      return { error: true };
    } catch (error) {
      console.error('Error updating subitem batch:', error);
      return { error: true };
    }
  },
  updateFile: (
    id: string,
    data: EditorMediaFile,
    type: string,
    parentId?: string
  ) => {
    // make an optimistic update then persist and re-fetch authoritative sequence
    const state = get();
    // ensure field is always a string (avoid boolean/union inference)
    let field = 'video_ids';
    let normalizedType = type;
    if (type === 'documents') {
      field = 'document_ids';
    } else if (type === 'pictures' || type === 'PHONE' || type == 'CARD') {
      field = 'picture_ids';
      normalizedType = 'pictures';
    } else if (type === 'audios') {
      field = 'audio_ids';
    }

    if (type === 'pictures' && data && data.id) {
      const collectBackgroundPictures = (items: SequenceItem[]): string[] => {
        const result: string[] = [];
        items.forEach(it => {
          if (it.type === 'BACKGROUND' && it.pictures) {
            (it.pictures as EditorMediaFile[]).forEach(p => {
              if (p && p.id) result.push(String(p.id));
            });
          }
          if (it.items) result.push(...collectBackgroundPictures(it.items));
        });
        return result;
      };
      const allBgPictureIds = collectBackgroundPictures(state.sequence!.items);
      const newId = String(data.id);
      const replacingSame = String(id) === newId;
      if (!replacingSame && allBgPictureIds.includes(newId)) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'You are unable to have duplicate background pictures.'
        });
        return;
      }
    }

    const findParentElement = (
      items: SequenceItem[],
      targetId: string,
      ttype: string
    ): string | undefined => {
      if (parentId) {
        const parentItem = items.find(item => item.id == parentId);
        if (parentItem) {
          return parentItem.id as string;
        }
      }

      for (const item of items) {
        if (
          item[ttype] &&
          (item[ttype] as EditorMediaFile[]).some(
            subItem => subItem.id == targetId
          )
        ) {
          return item.id as string;
        }
        if (item.items) {
          const found = findParentElement(item.items, targetId, ttype);
          if (found) return found;
        }
      }
      return undefined;
    };

    const getBackgroundId = (
      items: SequenceItem[],
      fileId: string
    ): string | undefined => {
      for (const item of items) {
        if (item.type === 'BACKGROUND' && item[normalizedType]) {
          const hasFile = (item[normalizedType] as EditorMediaFile[]).some(
            file => file.id === fileId
          );
          if (hasFile) return item.id as string;
        }
      }
      return undefined;
    };

    let backgroundId = getBackgroundId(state.sequence!.items, id);
    const parentElementId = findParentElement(
      state.sequence!.items,
      id,
      normalizedType
    );

    // Check if the file being updated belongs to a background item
    const isUpdatingBackgroundFile = (
      items: SequenceItem[],
      fileId: string
    ): boolean => {
      for (const item of items) {
        if (item.type === 'BACKGROUND' && item[normalizedType]) {
          const hasFile = (item[normalizedType] as EditorMediaFile[]).some(
            file => file.id === fileId
          );
          if (hasFile) return true;
        }
      }
      return false;
    };

    const isTargetBackground = isUpdatingBackgroundFile(
      state.sequence!.items || [],
      id
    );

    const _history = state.history;

    _history.forEach(item => {
      if (item.sequenceId == state.sequence!.id) {
        item.active = false;
      }
    });

    if (parentElementId && parentElementId != backgroundId) {
      backgroundId = undefined;
    }

    const GetFiles = (
      items: SequenceItem[],
      targetBackgroundId: string
    ): string[] => {
      const targetBackground = items.find(
        item => item.id === targetBackgroundId
      );
      if (targetBackground && targetBackground[normalizedType]) {
        return (targetBackground[normalizedType] as EditorMediaFile[]).map(
          file => file.id as string
        );
      }
      return [];
    };

    const updateFiles = (items: SequenceItem[]): SequenceItem[] => {
      return items.map(item => {
        if (item.type === 'BACKGROUND' && item[normalizedType]) {
          const existingHistoryItem = _history.find(
            historyItem =>
              historyItem.sequenceId == state.sequence!.id &&
              historyItem.elementId == item.id &&
              historyItem.fieldType == normalizedType &&
              historyItem.value &&
              Array.isArray(historyItem.value) &&
              (historyItem.value as EditorMediaFile[]).some(
                file => file.id == data.id
              )
          );

          if (
            !_history.find(
              historyItem => historyItem.sequenceId == state.sequence!.id
            )
          ) {
            _history.push({
              sequenceId: state.sequence!.id,
              elementId: undefined,
              fieldType: undefined,
              value: undefined,
              oldValue: undefined,
              type: undefined,
              active: false,
              timestamp: Date.now()
            });
          }

          if (!existingHistoryItem) {
            _history.push({
              sequenceId: state.sequence!.id,
              elementId: item.id,
              fieldType: normalizedType,
              value: (item[normalizedType] as EditorMediaFile[]).map(file => {
                return file.id == id ? data : file;
              }),
              oldValue: item[normalizedType],
              type: normalizedType,
              active: true,
              timestamp: Date.now()
            });
          }

          return {
            ...item,
            [normalizedType]: (item[normalizedType] as EditorMediaFile[]).map(
              file => (file.id == id ? data : file)
            )
          };
        }
        return item.items ? { ...item, items: updateFiles(item.items) } : item;
      });
    };

    const updateNonBackgroundFiles = (
      items: SequenceItem[]
    ): SequenceItem[] => {
      return items.map(item => {
        if (item.id === parentElementId || item.id === id) {
          const existingHistoryItem = _history.find(
            historyItem =>
              historyItem.sequenceId == state.sequence!.id &&
              historyItem.elementId == item.id &&
              historyItem.fieldType == normalizedType &&
              historyItem.value &&
              (historyItem.value as EditorMediaFile[]).length > 0 &&
              (historyItem.value as EditorMediaFile[])[0].id == data.id
          );

          if (
            !_history.find(
              historyItem => historyItem.sequenceId == state.sequence!.id
            )
          ) {
            _history.push({
              sequenceId: state.sequence!.id,
              elementId: undefined,
              fieldType: undefined,
              value: undefined,
              oldValue: undefined,
              type: undefined,
              active: false,
              timestamp: Date.now()
            });
          }

          if (!existingHistoryItem) {
            _history.push({
              sequenceId: state.sequence!.id,
              elementId: item.id,
              fieldType: normalizedType,
              value: [data],
              oldValue: item[normalizedType] || [],
              type: normalizedType,
              active: true,
              timestamp: Date.now()
            });
          }

          return { ...item, [normalizedType]: [data] };
        }
        return item.items
          ? { ...item, items: updateNonBackgroundFiles(item.items) }
          : item;
      });
    };

    const useBackgroundUpdate = backgroundId && isTargetBackground;

    const updatedSequence = preserveLegallyRequired(
      {
        ...state.sequence!,
        items: useBackgroundUpdate
          ? updateFiles(state.sequence!.items)
          : updateNonBackgroundFiles(state.sequence!.items)
      },
      state.sequence!.id,
      state.training
    );

    const updatedTraining = {
      ...state.training!,
      chapters: state.training!.chapters.map(chapter => ({
        ...chapter,
        chapterSequences: chapter.chapterSequences.map(seq => {
          if (seq.sequence.id === state.sequence!.id) {
            return { ...seq, sequence: updatedSequence };
          }
          return seq;
        })
      }))
    };

    // persist and re-fetch authoritative sequence
    (async () => {
      try {
        // Preload image before updating state to prevent flicker
        if (normalizedType === 'pictures' && data?.mediaUrl) {
          const mediaUrl = data.mediaUrl;
          await new Promise<void>(resolve => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve(); // Continue even on error
            img.src = mediaUrl;
            // Timeout fallback in case image takes too long
            setTimeout(() => resolve(), 2000);
          });
        }

        // optimistic set (after preload for pictures)
        set({
          ...state,
          sequence: updatedSequence,
          training: touchContentEditedAt(updatedTraining)
        });

        if (!state.apiService) return;

        if (useBackgroundUpdate && backgroundId) {
          await state.apiService.updateSubItem(
            backgroundId,
            field,
            GetFiles(updatedSequence.items, backgroundId)
          );
        } else {
          const elId = parentElementId || id;
          if (elId) {
            await state.apiService.updateSubItem(elId, field, [data.id]);
          }
        }

        const seq = await state.apiService.getSequenceInfo(state.sequence!.id);
        if (seq && seq.sequence) {
          extractAndStoreMetadata(seq, state);

          // replace sequence and update training copy
          const fresh = seq.sequence;

          const enrichedFresh = preserveLegallyRequired(
            fresh,
            fresh.id,
            get().training
          );

          const currentTraining = get().training;
          const freshTraining = {
            ...currentTraining!,
            chapters: currentTraining!.chapters.map(chapter => ({
              ...chapter,
              chapterSequences: chapter.chapterSequences.map(s =>
                s.sequence.id === enrichedFresh.id
                  ? { ...s, sequence: enrichedFresh }
                  : s
              )
            }))
          };
          saveHistoryToStorage(_history);
          set({
            ...get(),
            sequence: enrichedFresh,
            training: touchContentEditedAt(freshTraining),
            history: _history
          });
        }
      } catch (e) {
        // ignore persistence errors for now
        console.error('updateFile persistence error', e);
      }
    })();
  },
  setNarration: (audio?: AudioFile) =>
    set(state => {
      if (!state.apiService) {
        console.error('ApiService is not initialized.');
        return state;
      }

      state.apiService
        .setNarration(state.sequence!.id, audio!.id as number)
        .then(updatedSequence => {
          if (updatedSequence && updatedSequence.sequence) {
            const updatedTraining = {
              ...state.training!,
              chapters: state.training!.chapters.map(chapter => ({
                ...chapter,
                chapterSequences: chapter.chapterSequences.map(seq => {
                  return seq.sequence.id ===
                    (updatedSequence.sequence as EditorSequence).id
                    ? {
                        ...seq,
                        sequence: updatedSequence.sequence as EditorSequence
                      }
                    : seq;
                })
              }))
            };

            set({
              ...state,
              sequence: updatedSequence.sequence,
              training: updatedTraining
            });
          }
        });

      return { ...state };
    }),

  createNewChapter: (chapterName: string) =>
    set(state => {
      if (!state.apiService) {
        console.error('ApiService is not initialized.');
        return state;
      }

      if (!chapterName) {
        console.error('Data is not valid.');
        return state;
      }

      const maxPosition = state.training!.chapters.reduce(
        (max: number, chapter) => {
          return chapter.position! > max ? chapter.position! : max;
        },
        0
      );

      const data = {
        title: chapterName,
        training_id: state.training!.id,
        duration: 0,
        position: maxPosition + 1
      };

      state.apiService.createNewChapter(data).then(newChapter => {
        state.training!.chapters.push(newChapter.chapter);
        state.lastChapterId = newChapter.chapter.id;
        set({
          ...state,
          training: touchContentEditedAt(state.training!),
          lastChapterId: state.lastChapterId
        });
      });

      return { ...state };
    }),

  removeHistoryForSequence: (sequenceId: number) =>
    set(state => {
      const _history = state.history.filter(
        item => item.sequenceId != sequenceId
      );

      saveHistoryToStorage(_history);
      set({ history: _history });
      return { ...state };
    }),

  updateChapter: (chapterId: number, data: Record<string, unknown>) =>
    set(state => {
      if (!state.apiService) {
        console.error('ApiService is not initialized.');
        return state;
      }

      state.apiService.updateChapter(chapterId, data);

      return { ...state };
    }),

  setAIModificationStatus: (status: Partial<AIModificationStatus>) =>
    set(state => ({
      ...state,
      aiModification: {
        ...state.aiModification,
        ...status
      }
    })),

  resetAIModificationStatus: () =>
    set(state => ({
      ...state,
      aiModification: {
        ...state.aiModification,
        isLoading: false,
        error: null,
        success: false
      }
    })),

  modifySequenceWithAI: async (instructions: string) => {
    const state = get();
    const { setAIModificationStatus } = get();

    if (!state.apiService) {
      const error = 'ApiService is not initialized.';
      setAIModificationStatus({ isLoading: false, error, success: false });
      return { error: true, message: error };
    }
    if (!state.sequence?.id) {
      const error = 'No current sequence found.';
      setAIModificationStatus({ isLoading: false, error, success: false });
      return { error: true, message: error };
    }

    const trimmedInstructions = instructions.trim();

    if (!trimmedInstructions) {
      const error = 'Instructions cannot be empty.';
      setAIModificationStatus({ isLoading: false, error, success: false });
      return { error: true, message: error };
    }

    setAIModificationStatus({
      isLoading: true,
      error: null,
      success: false,
      lastPrompt: instructions
    });

    try {
      const startResponse = await state.apiService.startAIModification(
        state.sequence.id,
        trimmedInstructions
      );

      if (startResponse.error) {
        setAIModificationStatus({
          isLoading: false,
          error: startResponse.message || 'Failed to start AI modification',
          success: false
        });
        return startResponse;
      }

      const { ai_response_id } = startResponse;
      if (!ai_response_id) {
        const error = 'No AI response ID received';
        setAIModificationStatus({ isLoading: false, error, success: false });
        return { error: true, message: error };
      }

      return new Promise(resolve => {
        const pollingInterval = 2000; // 2 seconds
        const maxAttempts = 150; // 5 minutes max
        let attempts = 0;

        const pollInterval = setInterval(async () => {
          attempts++;

          try {
            const currentState = get();
            if (!currentState.apiService || !currentState.sequence?.id) {
              clearInterval(pollInterval);
              const error = 'Session lost';
              setAIModificationStatus({
                isLoading: false,
                error,
                success: false
              });
              resolve({ error: true, message: error });
              return;
            }

            const statusResponse =
              await currentState.apiService.pollAIModificationStatus(
                currentState.sequence.id,
                ai_response_id
              );

            if (statusResponse.error) {
              clearInterval(pollInterval);
              setAIModificationStatus({
                isLoading: false,
                error: statusResponse.message || 'Error checking status',
                success: false
              });
              resolve(statusResponse);
              return;
            }
            const { status, error } = statusResponse;

            if (status === 'completed') {
              clearInterval(pollInterval);

              try {
                const sequenceResponse =
                  await currentState.apiService.getSequenceInfo(
                    currentState.sequence.id
                  );

                extractAndStoreMetadata(sequenceResponse, currentState);

                if (sequenceResponse.sequence) {
                  const sequence = sequenceResponse.sequence as EditorSequence;
                  const updatedTraining = {
                    ...currentState.training!,
                    chapters: currentState.training!.chapters.map(chapter => ({
                      ...chapter,
                      chapterSequences: chapter.chapterSequences.map(seq =>
                        seq.sequence.id === currentState.sequence!.id
                          ? { ...seq, sequence }
                          : seq
                      )
                    }))
                  };

                  const filteredHistory = currentState.history.filter(
                    item => item.sequenceId !== currentState.sequence!.id
                  );
                  saveHistoryToStorage(filteredHistory);

                  set({
                    sequence,
                    training: updatedTraining,
                    history: filteredHistory,
                    aiModification: {
                      isLoading: false,
                      error: null,
                      success: true,
                      lastPrompt: currentState.aiModification.lastPrompt
                    }
                  });

                  resolve({ success: true, sequence });
                } else {
                  const error = 'Failed to fetch updated sequence';
                  setAIModificationStatus({
                    isLoading: false,
                    error,
                    success: false
                  });
                  resolve({ error: true, message: error });
                }
              } catch (fetchError) {
                const error =
                  (fetchError as Error).message ||
                  'Failed to fetch updated sequence';
                setAIModificationStatus({
                  isLoading: false,
                  error,
                  success: false
                });
                resolve({ error: true, message: error });
              }
              return;
            }

            if (status === 'failed') {
              clearInterval(pollInterval);
              const errorMsg = error || 'AI modification failed';
              setAIModificationStatus({
                isLoading: false,
                error: errorMsg,
                success: false
              });
              resolve({ error: true, message: errorMsg });
              return;
            }

            if (attempts >= maxAttempts) {
              clearInterval(pollInterval);
              const timeoutError = 'AI modification timed out';
              setAIModificationStatus({
                isLoading: false,
                error: timeoutError,
                success: false
              });
              resolve({ error: true, message: timeoutError });
              return;
            }
          } catch (pollError) {
            clearInterval(pollInterval);
            const error =
              (pollError as Error).message || 'Network error during polling';
            setAIModificationStatus({
              isLoading: false,
              error,
              success: false
            });
            resolve({ error: true, message: error });
          }
        }, pollingInterval);
      });
    } catch (error) {
      const errorMsg =
        (error as Error).message || 'Failed to start AI modification';
      setAIModificationStatus({
        isLoading: false,
        error: errorMsg,
        success: false
      });
      return { error: true, message: errorMsg };
    }
  },
  updateTrainingTitle: (title: string) => {
    const state = get();

    state.apiService?.updateTrainingTitle(state.training!.id as number, title);
    set(state => ({
      ...state,
      training: { ...state.training!, title }
    }));
  },
  shouldDisplayUpdateTranslationsModal: false,
  setDisplayUpdateTranslationsModal: (display: boolean) =>
    set({ shouldDisplayUpdateTranslationsModal: display }),
  shouldAutoUpdateTranslations: false,
  setShouldAutoUpdateTranslations: (autoUpdate: boolean) =>
    set({ shouldAutoUpdateTranslations: autoUpdate })
}));

export const getEditorState = () => useEditorStore.getState();

interface EditorProviderProps {
  children: ReactNode;
  editorMode?: boolean;
}

export const EditorProvider: FC<EditorProviderProps> = ({
  children,
  editorMode = false
}) => {
  const setEditorMode = useEditorStore(state => state.setEditorMode);

  React.useEffect(() => {
    setEditorMode(editorMode);
  }, [editorMode, setEditorMode]);

  return <>{children}</>;
};
